import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Building2,
  Plus,
  Loader2,
  AlertCircle,
  Copy,
  CheckCircle,
  Eye,
  UserPlus,
  Users,
  Search,
  Key,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface Church {
  id: string
  name: string
  email: string
  phone: string
  church_code: string | null
  pastor_name: string | null
  address: string | null
  created_at: string
  admin_count?: number
}

interface ChurchesListProps {
  user: AdminUser | null
}

export default function ChurchesList({ user }: ChurchesListProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [churches, setChurches] = useState<Church[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  // Admin creation dialog state
  const [showCreateAdminDialog, setShowCreateAdminDialog] = useState(false)
  const [selectedChurch, setSelectedChurch] = useState<Church | null>(null)
  const [creatingAdmin, setCreatingAdmin] = useState(false)
  const [adminForm, setAdminForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
  })

  // Church admins view dialog
  const [showAdminsDialog, setShowAdminsDialog] = useState(false)
  const [churchAdmins, setChurchAdmins] = useState<any[]>([])
  const [loadingAdmins, setLoadingAdmins] = useState(false)

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to view churches.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadChurches()
  }, [])

  async function loadChurches() {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('churches')
        .select(`
          *,
          profiles!profiles_church_id_fkey(count)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      setChurches(data || [])
    } catch (error) {
      console.error('Error loading churches:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadChurchAdmins(churchId: string) {
    try {
      setLoadingAdmins(true)

      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, phone, admin_level, role, created_at')
        .eq('church_id', churchId)
        .in('admin_level', ['church_owner', 'church_admin'])

      if (error) throw error

      setChurchAdmins(data || [])
    } catch (error) {
      console.error('Error loading church admins:', error)
    } finally {
      setLoadingAdmins(false)
    }
  }

  function copyChurchCode(code: string) {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  function openCreateAdminDialog(church: Church) {
    setSelectedChurch(church)
    setAdminForm({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
    })
    setShowCreateAdminDialog(true)
  }

  async function openViewAdminsDialog(church: Church) {
    setSelectedChurch(church)
    setShowAdminsDialog(true)
    await loadChurchAdmins(church.id)
  }

  async function handleCreateAdmin() {
    if (!selectedChurch) return

    try {
      setCreatingAdmin(true)

      // Check if email already exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, email, church_id, admin_level')
        .eq('email', adminForm.email)
        .single()

      if (existingProfile) {
        // Update existing profile
        if (existingProfile.church_id && existingProfile.church_id !== selectedChurch.id) {
          if (!confirm(`This user is already associated with another church. Do you want to transfer them to ${selectedChurch.name}?`)) {
            return
          }
        }

        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            church_id: selectedChurch.id,
            admin_level: 'church_admin',
            role: 'admin',
            first_name: adminForm.first_name || existingProfile.first_name,
            last_name: adminForm.last_name || existingProfile.last_name,
            phone: adminForm.phone || existingProfile.phone,
          })
          .eq('id', existingProfile.id)

        if (updateError) throw updateError

        alert(`✅ User upgraded to Church Admin for ${selectedChurch.name}!\n\nThey can now login with: ${adminForm.email}`)
      } else {
        // User needs to create an account first
        alert(
          `📧 Admin Invitation Created\n\n` +
          `Send these instructions to ${adminForm.email}:\n\n` +
          `1. Go to the login page and sign up\n` +
          `2. Use email: ${adminForm.email}\n` +
          `3. After signup, contact platform admin to activate admin access\n\n` +
          `Church: ${selectedChurch.name}\n` +
          `Church Code: ${selectedChurch.church_code || 'Not generated'}\n\n` +
          `Note: You'll need to manually update their profile after they sign up:\n` +
          `- church_id: ${selectedChurch.id}\n` +
          `- admin_level: church_admin\n` +
          `- role: admin`
        )

        // TODO: Create invite_tokens record for proper invitation flow
      }

      setShowCreateAdminDialog(false)
      setAdminForm({ email: '', first_name: '', last_name: '', phone: '' })
    } catch (error: any) {
      console.error('Error creating admin:', error)
      alert('Failed to create admin: ' + error.message)
    } finally {
      setCreatingAdmin(false)
    }
  }

  const filteredChurches = churches.filter(church =>
    church.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    church.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    church.church_code?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading churches...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Churches</h1>
          <p className="text-muted-foreground">Manage all churches on the platform</p>
        </div>
        <Button onClick={() => navigate('/platform/invitations')}>
          <Plus className="h-4 w-4 mr-2" />
          New Church
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Churches Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Churches ({filteredChurches.length})</CardTitle>
          <CardDescription>View and manage church accounts</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Church Name</TableHead>
                <TableHead>Church Code</TableHead>
                <TableHead>Pastor</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredChurches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No churches found matching your search.' : 'No churches yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredChurches.map((church) => (
                  <TableRow key={church.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{church.name}</div>
                          <div className="text-sm text-muted-foreground">{church.address || 'No address'}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {church.church_code ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono">
                            <Key className="h-3 w-3 mr-1" />
                            {church.church_code}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyChurchCode(church.church_code!)}
                            className="h-6 w-6 p-0"
                          >
                            {copiedCode === church.church_code ? (
                              <CheckCircle className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">No code</Badge>
                      )}
                    </TableCell>
                    <TableCell>{church.pastor_name || '-'}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{church.email}</div>
                        <div className="text-muted-foreground">{church.phone}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(church.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openViewAdminsDialog(church)}
                          title="View Admins"
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCreateAdminDialog(church)}
                          title="Create Admin"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/platform/churches/${church.id}`)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdminDialog} onOpenChange={setShowCreateAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Church Admin</DialogTitle>
            <DialogDescription>
              Add an admin for {selectedChurch?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin_email">Email *</Label>
              <Input
                id="admin_email"
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="admin@example.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin_first_name">First Name</Label>
                <Input
                  id="admin_first_name"
                  value={adminForm.first_name}
                  onChange={(e) => setAdminForm({ ...adminForm, first_name: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin_last_name">Last Name</Label>
                <Input
                  id="admin_last_name"
                  value={adminForm.last_name}
                  onChange={(e) => setAdminForm({ ...adminForm, last_name: e.target.value })}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_phone">Phone</Label>
              <Input
                id="admin_phone"
                value={adminForm.phone}
                onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
                placeholder="+233 XX XXX XXXX"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdminDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAdmin} disabled={creatingAdmin || !adminForm.email}>
              {creatingAdmin ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Admin
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Admins Dialog */}
      <Dialog open={showAdminsDialog} onOpenChange={setShowAdminsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Church Admins - {selectedChurch?.name}</DialogTitle>
            <DialogDescription>
              Manage administrators for this church
            </DialogDescription>
          </DialogHeader>

          {loadingAdmins ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : churchAdmins.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No admins found for this church.
            </div>
          ) : (
            <div className="space-y-2">
              {churchAdmins.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{admin.first_name} {admin.last_name}</div>
                    <div className="text-sm text-muted-foreground">{admin.email}</div>
                  </div>
                  <Badge variant={admin.admin_level === 'church_owner' ? 'default' : 'secondary'}>
                    {admin.admin_level === 'church_owner' ? 'Owner' : 'Admin'}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminsDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowAdminsDialog(false)
              if (selectedChurch) openCreateAdminDialog(selectedChurch)
            }}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
